import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  InteractionRequiredAuthError,
  type AccountInfo,
  type PublicClientApplication,
} from '@azure/msal-browser'
import { apiClient } from '../services/api'
import { loadAuthConfig, type AuthRuntimeConfig } from './authConfig'
import { apiTokenRequest, createMsalInstance, loginRequest } from './msal'

/**
 * True for the public event-CONTENT read paths that support admin draft preview
 * (the optional-attendee GETs in the events router). Deliberately EXCLUDES every
 * attendee-scoped `/events/*` sub-path (agenda, networking, checkin, concierge,
 * engagement, registration, push, and the attendee profile/photo endpoints),
 * which authenticate via the attendee session — attaching an Entra admin token
 * to those makes the backend prefer it over the attendee cookie and 401.
 */
function isAdminPreviewableEventRead(url: string): boolean {
  if (!url.startsWith('/events/')) return false
  // Attendee-scoped segments that must use the attendee session, not an
  // admin/Entra token.
  const ATTENDEE_SCOPED = [
    '/attendee',
    '/agenda',
    '/networking',
    '/meetings',
    '/checkin',
    '/concierge',
    '/polls',
    '/questions',
    '/register',
    '/registration',
    '/option-groups',
    '/push',
    '/sponsors/', // e.g. /sponsors/{id}/interest (POST) — attendee action
  ]
  return !ATTENDEE_SCOPED.some((seg) => url.includes(seg))
}

interface AuthState {
  ready: boolean
  authEnabled: boolean
  isAuthenticated: boolean
  account: AccountInfo | null
  roles: string[]
  name: string
  /** True while an MSAL redirect return is being processed (before MSAL
   *  navigates back to the original page). Callers should show a loading screen
   *  instead of the landing page to avoid a flash of "/" before /admin. */
  authRedirectInProgress: boolean
  /** Deep-link to this project in DevAssist, or '' when unconfigured. */
  devassistProjectUrl: string
  login: () => Promise<void>
  logout: () => void
  hasRole: (roles: string[]) => boolean
}

const AuthContext = createContext<AuthState | undefined>(undefined)

function rolesFromAccount(account: AccountInfo | null): string[] {
  const claims = (account?.idTokenClaims ?? {}) as { roles?: string[] }
  return claims.roles ?? []
}

/**
 * Synchronously detect that this page load is an MSAL redirect return, so the
 * very first render can show a loading screen (no flash of the landing page at
 * "/" before MSAL navigates back to /admin). MSAL returns the auth response in
 * the URL fragment as `#code=…&state=…` (or `#error=…`). We only treat it as an
 * auth redirect when a `state` param is present alongside code/error, to avoid
 * matching unrelated hashes.
 */
function isMsalRedirectReturn(): boolean {
  const hash = window.location.hash
  if (!hash || hash.length < 2) return false
  const params = new URLSearchParams(hash.replace(/^#/, ''))
  return (
    (params.has('code') || params.has('error')) && params.has('state')
  )
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false)
  // Initialized synchronously so the first render already knows we're mid-
  // redirect and shows a loading screen instead of flashing the landing page.
  const [authRedirectInProgress, setAuthRedirectInProgress] = useState(
    isMsalRedirectReturn,
  )
  const [cfg, setCfg] = useState<AuthRuntimeConfig | null>(null)
  const [account, setAccount] = useState<AccountInfo | null>(null)
  // Roles as seen by the API (decoded from the access token). This is the
  // authoritative source and matches what the server enforces — the ID-token
  // `roles` claim is not always populated for API-app app-roles.
  const [serverRoles, setServerRoles] = useState<string[] | null>(null)
  const msalRef = useRef<PublicClientApplication | null>(null)
  // Hold the runtime config in a ref so the axios interceptor (registered once)
  // always reads the current config instead of a stale closure value.
  const cfgRef = useRef<AuthRuntimeConfig | null>(null)

  // Acquire an API access token silently (falling back to popup), used by the
  // axios interceptor to authorize admin calls.
  const getToken = useCallback(async (silentOnly = false): Promise<string | null> => {
    const msal = msalRef.current
    const config = cfgRef.current
    if (!msal || !config) return null
    const acct = msal.getActiveAccount() ?? msal.getAllAccounts()[0]
    if (!acct) return null
    try {
      const res = await msal.acquireTokenSilent({
        ...apiTokenRequest(config),
        account: acct,
      })
      return res.accessToken
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Silent-only callers (e.g. background /events reads for draft preview)
        // must never trigger interaction. For interactive admin calls, use the
        // redirect flow (COOP-immune; popup auth is blocked by COOP). This
        // navigates away and returns via handleRedirectPromise, so it resolves
        // null here — the caller retries after the redirect completes.
        if (silentOnly) return null
        await msal.acquireTokenRedirect(apiTokenRequest(config))
        return null
      }
      return null
    }
  }, [])

  // Fetch the caller's roles from the API (server-decoded access token).
  const refreshRoles = useCallback(async () => {
    try {
      const { data } = await apiClient.get<{ roles: string[] }>('/auth/me')
      setServerRoles(data.roles ?? [])
    } catch {
      setServerRoles([])
    }
  }, [])

  useEffect(() => {
    let active = true
    // Safety net: never let the app hang on the loading screen. If we chose to
    // hold `ready` for an MSAL redirect navigation that (for any reason) doesn't
    // fire, force the app to render after a short delay so a manual refresh is
    // never required. Cleared on unmount.
    const failsafe = setTimeout(() => {
      if (active) {
        setAuthRedirectInProgress(false)
        setReady(true)
      }
    }, 4000)
    ;(async () => {
      const config = await loadAuthConfig()
      if (!active) return
      cfgRef.current = config
      setCfg(config)

      const msal = await createMsalInstance(config)
      msalRef.current = msal

      if (msal) {
        // Attach a request interceptor that adds the bearer token to
        // admin/auth API calls. Registered before any /auth/me call.
        //
        // Also attach it to public /events reads WHEN an admin is signed in, so
        // an admin previewing a draft event (via the attendee /e/{slug} view)
        // is recognized by the backend's optional-admin check and can see
        // unpublished events. For a plain attendee (no MSAL account) getToken()
        // returns null, so nothing is attached and the anonymous path is used.
        apiClient.interceptors.request.use(async (req) => {
          const url = req.url ?? ''
          const isAdminCall = url.startsWith('/admin') || url.startsWith('/auth/me')
          if (isAdminCall) {
            const token = await getToken()
            if (token) {
              req.headers = req.headers ?? {}
              req.headers.Authorization = `Bearer ${token}`
            }
          } else if (
            isAdminPreviewableEventRead(url) &&
            (msalRef.current?.getActiveAccount() ??
              msalRef.current?.getAllAccounts()[0])
          ) {
            // Admin previewing a draft event via the attendee /e/{slug} view:
            // attach the Entra token so the backend's optional-admin check lets
            // them see unpublished content. Only for the public event-CONTENT
            // reads (event detail, sessions, speakers, content, forms, pages) —
            // NEVER for attendee-scoped endpoints (agenda, networking, checkin,
            // concierge, engagement, register, push, attendee profile/photo),
            // which authenticate via the attendee session. Attaching an Entra
            // token to those made the backend prefer it over the attendee cookie
            // and reject the request (401) whenever an admin was ALSO signed in
            // in the same browser (e.g. clicking an attendee invite link).
            const token = await getToken(true)
            if (token) {
              req.headers = req.headers ?? {}
              req.headers.Authorization = `Bearer ${token}`
            }
          }
          return req
        })

        // Whether THIS page load carries the auth response in the URL (the
        // intermediate return page, e.g. "/#code=…&state=…"). Captured before
        // handleRedirectPromise consumes/clears the hash. Only on this page will
        // MSAL navigate away (navigateToLoginRequestUrl) to the original request
        // URL (e.g. /admin) — so this is the only case where we hold the loading
        // screen. On the final landing page there is no hash, so we complete
        // normally (this was the "stuck on Checking your access" bug).
        const isRedirectReturn = isMsalRedirectReturn()

        // Handle redirect returns and hydrate the active account.
        const redirectResult = await msal
          .handleRedirectPromise()
          .catch(() => null)
        const existing =
          redirectResult?.account ?? msal.getAllAccounts()[0] ?? null
        if (existing) {
          msal.setActiveAccount(existing)
          setAccount(existing)
          await refreshRoles()
        }
        if (isRedirectReturn && redirectResult && active) {
          // On the intermediate return page and MSAL is about to navigate to the
          // original URL — keep the loading screen up (don't flip `ready`) so the
          // landing page never flashes before that navigation lands.
          return
        }
        // Terminal page (or no redirect): clear the flag and finish init.
        if (active) setAuthRedirectInProgress(false)
      } else if (active) {
        setAuthRedirectInProgress(false)
      }
      if (active) setReady(true)
    })()
    return () => {
      active = false
      clearTimeout(failsafe)
    }
    // getToken depends on cfg; we intentionally run this once on mount and
    // rebuild the interceptor closure via the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const login = useCallback(async () => {
    const msal = msalRef.current
    if (!msal) return
    // Redirect flow (not popup): popup auth polls popupWindow.closed, which the
    // browser blocks under Cross-Origin-Opener-Policy, breaking sign-in with
    // "user_cancelled". Redirect navigates the whole page to Entra and back;
    // handleRedirectPromise() (in the init effect) then completes sign-in.
    await msal.loginRedirect(loginRequest)
  }, [])

  const logout = useCallback(() => {
    const msal = msalRef.current
    if (!msal) return
    // Redirect flow (not popup): popup logout has the same COOP window.closed
    // problem as popup login. logoutRedirect navigates the whole page to Entra
    // and back to postLogoutRedirectUri.
    setAccount(null)
    setServerRoles(null)
    void msal.logoutRedirect()
  }, [])

  // Prefer server-decoded roles; fall back to ID-token claims until they load.
  const roles = useMemo(
    () => serverRoles ?? rolesFromAccount(account),
    [serverRoles, account],
  )

  const value = useMemo<AuthState>(
    () => ({
      ready,
      authEnabled: Boolean(cfg?.authEnabled),
      isAuthenticated: Boolean(account),
      account,
      roles,
      name: account?.name ?? '',
      authRedirectInProgress,
      devassistProjectUrl: cfg?.devassistProjectUrl ?? '',
      login,
      logout,
      hasRole: (allowed: string[]) => allowed.some((r) => roles.includes(r)),
    }),
    [ready, cfg, account, roles, login, logout, authRedirectInProgress],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
